import _ from 'lodash'
import { createSelector } from 'reselect'

import DEFAULTS from 'core/data-model/constants/defaults'
import VALUES from 'core/data-model/constants/values'
import { validateBasicPremium } from 'core/service/basic-plan/validation'
import { isValidAge } from 'core/service/insured/birthdate'
import { isNil, isNotNil } from 'core/service/lib/type-check'
import { getDistributorGroups, getUserDistributorGroup } from 'identity/selectors'
import {
  getFlexiHealthSelectedPlanCode,
  getFlexiHealthSelectedPlanName,
} from 'quick-quote/product-flexi-health/coverage-plan/selectors'
import {
  getIhealthySelectedPlanCode,
  getIhealthySelectedPlanName,
} from 'quick-quote/product-health/coverage-plan/selectors'
import {
  getIhealthyUltraSelectedPlanName,
  getIhealthyUltraSelectedPlanCode,
} from 'quick-quote/product-ihealthy-ultra/coverage-plan/selectors'
import { getBasicPlanCodeForMRTA } from 'quick-quote/product-mrta/coverage-plan/selectors.utils'
import {
  getICareSelectedPlanCode,
  getICareSelectedPlanName,
} from 'quick-quote/product-protection/coverage-plan/selectors'
import { getSelectedDisplayProduct, getSelectedDisplayProductCode } from 'quick-quote/product-selection/selectors'

export const getSumAssured = (state) => state.coveragePlan.selectedBasicPlan.sumAssured.value
export const getSumAssured$ = (state) => state.coveragePlan.selectedBasicPlan.sumAssured
export const getBasicPremium = (state) => state.coveragePlan.selectedBasicPlan.premium
export const getFullBasicPremium = (state) => state.coveragePlan.selectedBasicPlan.fullBasicPremium
export const getFullAnnualBasicPremium = (state) => state.coveragePlan.selectedBasicPlan.fullAnnualBasicPremium
export const getCampaignDiscountMessage = (state) => state.coveragePlan.selectedBasicPlan.campaignDiscountMessage
export const getCampaignDiscountEndDate = (state) => state.coveragePlan.selectedBasicPlan.campaignDiscountEndDate
export const getBasicPlanErrorMessage = (state) => state.coveragePlan.selectedBasicPlan.error
export const getBasicPremiumState = (state) => state.coveragePlan.premiumState
export const getTotalPremium$ = (state) => state.coveragePlan.totalPremium
export const getPensionType = (state) => state.coveragePlan.pensionType
export const haveBasicPremiumState = (state) => state.coveragePlan.premiumState !== -1
export const haveTotalPremiumState = (state) => state.coveragePlan.totalPremium.value !== -1
export const getTotalRiderPremium = (state) => state.coveragePlan.totalRiderPremium
export const getRiders = (state) => state.riders.payload
export const getRidersStatus = (state) => state.riders.status
export const getModelFactors = (state) => state.modelFactors.payload
export const getModelFactorsStatus = (state) => state.modelFactors.status
export const getSelectedModelFactorID = (state) => state.coveragePlan.selectedModelFactorID
export const getcoveragePlanStartDate = (state) => state.coveragePlan.startDate
export const getSelectedPlan = (state) => state.coveragePlan.selectedPlan
export const getOccupationFactor = (state) => state.coveragePlan.occupationFactor

export const getSelectedBasicCoveragePlan = createSelector(
  (state) => state.coveragePlan.selectedBasicPlan,
  getSelectedDisplayProductCode,
  (basicPlan, basicPlanCode) => ({
    ...basicPlan,
    code: basicPlanCode,
  })
)

export const getSelectedModelFactor = createSelector(
  getModelFactors,
  getSelectedModelFactorID,
  (modelFactors, selectedModelFactorID) => _.find(modelFactors, { id: selectedModelFactorID })
)

export const getSelectedModelFactorValue = createSelector(getSelectedModelFactor, (modelFactor) =>
  modelFactor ? modelFactor.factor : 1
)

export const getSelectedModelFactorPeriod = createSelector(getSelectedModelFactor, (selectedModelFactor) =>
  selectedModelFactor ? selectedModelFactor.periods : 0
)

export const getSelectedModelFactorLabel = createSelector(getSelectedModelFactor, (selectedModelFactor) =>
  _.get(selectedModelFactor, 'displayLabel', '-')
)

export const getAvailableModelFactors = createSelector(
  getSelectedDisplayProduct,
  getModelFactors,
  (basicPlan, modelFactors) => modelFactors.filter((modelFactor) => basicPlan.modelFactors.indexOf(modelFactor.id) > -1)
)

export const getOptionsLoanPeriod = createSelector(getSelectedDisplayProduct, (basicPlan) => {
  switch (getBasicPlanCodeForMRTA(basicPlan)) {
    case 'MRTA_SIMPLY':
    case 'MRTA_PLUS':
    case 'MRTA_FULL':
    case 'MRTA_TRUNCATE':
      return DEFAULTS.DEFAULT_LOAN_PERIOD.MRTA
    case 'GLTSP':
      return DEFAULTS.DEFAULT_LOAN_PERIOD.GLTSP
    default:
      return []
  }
})

export const getModelFactorsForUser = (state) => {
  const basicPlan = getSelectedDisplayProduct(state)
  const userDistributorGroup = getUserDistributorGroup(state)
  const distributorGroups = getDistributorGroups(state)
  let modelFactors = getModelFactors(state)
  modelFactors = modelFactors.filter((modelFactor) => basicPlan.modelFactors.indexOf(modelFactor.id) > -1)
  const distributorGroupForUser = distributorGroups.find(
    (distributorGroup) => distributorGroup.id === userDistributorGroup
  )
  if (distributorGroupForUser) {
    const result = modelFactors.filter((modelFactor) => distributorGroupForUser.modelFactors.includes(modelFactor.id))
    return result
  }
  return modelFactors
}

export const getReadOnlyFields = createSelector(getSelectedDisplayProduct, (basicPlan) => basicPlan.readOnlyFields)

export const getBasicPremium$ = createSelector(
  getBasicPremiumState,
  (state) => state.coveragePlan.selectedBasicPlan.premium,
  getCampaignDiscountEndDate,
  (basicPremiumState, basicPremium, campaignDiscountEndDate) =>
    validateBasicPremium({ basicPremiumState, campaignDiscountEndDate }, basicPremium)
)

export const getTotalPremium = createSelector(getTotalPremium$, (totalPremium) => {
  if (isNil(totalPremium)) {
    return 0
  }
  return totalPremium.value
})

export const getAvailableRidersByCode = (state) => state.coveragePlan.riders

export const getAvailableRiderCodes = createSelector(getAvailableRidersByCode, (table) => {
  return Object.keys(table).sort(
    (a, b) => DEFAULTS.RIDERS_SORT_ORDER.indexOf(a) - DEFAULTS.RIDERS_SORT_ORDER.indexOf(b)
  )
})

export const getAvailableRiders = createSelector(getAvailableRiderCodes, getAvailableRidersByCode, (codes, table) =>
  codes.map((code) => table[code]).filter(isNotNil)
)

export const getSelectedRiders = createSelector(getAvailableRiders, (availableRiders) =>
  availableRiders.filter((rider) => rider.isSelected && rider.isSelectable)
)

export const hasMHPRiderAvailableRider = createSelector(getAvailableRiders, (availableRiders) => {
  const riderMHP = _.find(availableRiders, (rider) => rider.code === 'MHP')
  return riderMHP ? true : false
})

export const getRiderInAvailableRider = (riderCode) =>
  createSelector(getAvailableRiders, (availableRiders) => {
    return _.find(availableRiders, (rider) => rider.code === riderCode)
  })

export const getAvaliableOptionByAge = (rider, insuredAge) => {
  const result = rider.options[0].values.filter((r) => {
    if (!r.allowAge) return true
    const entryAge = {
      minimum: r.allowAge.minimumValue,
      maximum: r.allowAge.maximumValue,
    }
    return isValidAge(insuredAge.value, insuredAge.unit, entryAge)
  })
  return result
}

export const getActualRiderPlanCodeWithPackageRiderByAge = (rider, insuredAge) => {
  if (!rider.packageRiders) return rider.defaultRiderPlanCode ?? ''

  const planAvaliableOptions = rider.packageRiders.filter((plan) => {
    if (!plan.allowAge) return true
    const entryAge = {
      minimum: plan.allowAge.minimumValue,
      maximum: plan.allowAge.maximumValue,
    }
    return isValidAge(insuredAge.value, insuredAge.unit, entryAge)
  })

  return planAvaliableOptions.length > 0 ? planAvaliableOptions[0].planGroupCode : rider.defaultRiderPlanCode ?? ''
}

export const getValidSelectedRiders = createSelector(getSelectedRiders, (selectedRiders) => {
  return selectedRiders.filter((rider) =>
    isNil(
      rider.errors.find(
        (error) => ![VALUES.ADDITIONAL_INFORMATION, VALUES.RIDER_CAMPAIGN_INFORMATION].includes(error.type)
      )
    )
  )
})

export const getSelectedPlanName = (state) => {
  switch (state.selectedDisplayProduct.category) {
    case VALUES.HEALTH:
      return getIhealthySelectedPlanName(state)
    case VALUES.PROTECTION:
      return getICareSelectedPlanName(state)
    case VALUES.FLEXI_HEALTH:
      return getFlexiHealthSelectedPlanName(state)
    case VALUES.IHEALTHY_ULTRA:
      return getIhealthyUltraSelectedPlanName(state)
    default:
      return ''
  }
}

export const getSelectedPlanCode = (state) => {
  switch (state.selectedDisplayProduct.category) {
    case VALUES.HEALTH:
      return getIhealthySelectedPlanCode(state)
    case VALUES.PROTECTION:
      return getICareSelectedPlanCode(state)
    case VALUES.FLEXI_HEALTH:
      return getFlexiHealthSelectedPlanCode(state)
    case VALUES.IHEALTHY_ULTRA:
      return getIhealthyUltraSelectedPlanCode(state)
    default:
      return ''
  }
}

export const _hasInvalidRiders = (selectedRiders) => {
  const selectedRiderInvalidMessages = selectedRiders.filter((rider) =>
    isNotNil(rider.errors.find((error) => error.type === VALUES.VALIDATION))
  )
  return selectedRiderInvalidMessages.length > 0
}

const hasInvalidRiders = createSelector(getSelectedRiders, _hasInvalidRiders)

export const _hasFinishedPremium = (
  sumAssured$,
  basicPremium,
  totalPremium$,
  foundInvalidRiders,
  basicPlanErrorMessage,
  selectedRiders,
  selectedDisplayProduct
) => {
  const finished =
    sumAssured$.isOk &&
    basicPremium.isOk &&
    totalPremium$.isOk &&
    !foundInvalidRiders &&
    _.isEmpty(basicPlanErrorMessage)
  if (selectedDisplayProduct.type === VALUES.CAMPAIGN_PRODUCT_TYPE) {
    const selectedRiderCodes = selectedRiders.map((rider) => rider.code)
    return (
      finished &&
      selectedDisplayProduct.requiredRiders != null &&
      selectedDisplayProduct.requiredRiders.every((riderCode) => {
        return selectedRiderCodes.indexOf(riderCode) > -1
      })
    )
  }
  return finished
}

export const getWealthDataSource = (state) => state.wealthInfo.dataSource

export const hasFinishedPremiumCalculation = createSelector(
  getSumAssured$,
  getBasicPremium$,
  getTotalPremium$,
  hasInvalidRiders,
  getBasicPlanErrorMessage,
  getSelectedRiders,
  getSelectedDisplayProduct,
  _hasFinishedPremium
)
